const DAY_KEY = /^\d{4}-\d{2}-\d{2}$/;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Default deeplinks BE stamps when redirection_link is omitted. */
export const DEFAULT_CHALLENGE_DEEPLINKS = {
  quiz: 'dugout://arcade/dugout-daily?gameId={id}',
  fastest_finger: 'dugout://arcade/dugout-daily?gameId={id}',
  super_over: 'dugout://arcade/dugout-daily/objectives?gameId={id}',
  run_connect: 'dugout://arcade/run-connect',
  snapshot: 'dugout://arcade/snap-shot',
  pitch_word: 'dugout://arcade/pitch-word',
};

/** Daily Page challenge game types — isolated via config.surface=daily_page. */
export const GAME_TYPES = [
  { value: 'quiz', label: 'Daily Quiz', notes: 'Exactly 5 questions · BE auto game_id + deeplink' },
  {
    value: 'fastest_finger',
    label: 'Daily Fastest Finger',
    notes: 'Exactly 5 free-text rounds · BE auto game_id + deeplink',
  },
  {
    value: 'super_over',
    label: 'Daily Super Over',
    notes: 'Daily Page only — never Arcade Super Over Daily',
  },
  { value: 'pitch_word', label: 'Pitch Word', notes: 'Launcher · deeplink auto if omitted' },
  { value: 'snapshot', label: 'Snapshot', notes: 'Launcher · deeplink auto if omitted' },
  { value: 'run_connect', label: 'Run Connect', notes: 'Launcher · deeplink auto if omitted' },
];

/** Primary Add buttons — keep Quiz / FF / Super Over forms separated. */
export const PRIMARY_CHALLENGE_GAME_TYPES = ['quiz', 'fastest_finger', 'super_over'];

export const DAILY_PAGE_SUPER_OVER_OBJECTIVES = [
  'chase_target',
  'hit_sixes',
  'consecutive_sixes',
  'score_in_balls',
  'take_wickets',
  'defend_runs',
  'no_boundaries',
  'no_dot_balls',
  'no_wicket_survival',
];

export const SECTOR_TYPES = [
  { value: 'fe_reward', label: 'FE reward' },
  { value: 'try_again', label: 'Try again' },
  { value: 'extra_spin', label: 'Extra spin' },
  { value: 'image', label: 'Image' },
];

export const DEFAULT_MILESTONES = [
  {
    days_required: 3,
    fan_energy_reward: 100,
    title: '3 days complete',
    message: 'Nice work — keep showing up',
    sort_order: 1,
    is_active: true,
  },
  {
    days_required: 5,
    fan_energy_reward: 150,
    title: '5 days complete',
    message: 'You are on a roll',
    sort_order: 2,
    is_active: true,
  },
  {
    days_required: 7,
    fan_energy_reward: 250,
    title: '7 days complete',
    message: 'Full cycle',
    sort_order: 3,
    is_active: true,
  },
];

export function isDayKey(value) {
  if (!DAY_KEY.test(String(value || ''))) return false;
  const [y, m, d] = value.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.getUTCFullYear() === y && date.getUTCMonth() === m - 1 && date.getUTCDate() === d;
}

export function formatIstDate(dayKey) {
  if (!isDayKey(dayKey)) return dayKey || '—';
  const [y, m, d] = dayKey.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return `${WEEKDAYS[date.getUTCDay()]}, ${String(d).padStart(2, '0')} ${MONTHS[m - 1]} ${y}`;
}

export function addDays(dayKey, days) {
  if (!isDayKey(dayKey)) return '';
  const [y, m, d] = dayKey.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d + days));
  return date.toISOString().slice(0, 10);
}

/** Current IST calendar day as YYYY-MM-DD. */
export function istToday() {
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  }
}

export function monthBounds(year, monthIndex) {
  const start = `${year}-${String(monthIndex + 1).padStart(2, '0')}-01`;
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  const end = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  return { start, end, lastDay };
}

export function statusColor(status) {
  if (status === 'live') return 'success';
  if (status === 'completed') return 'default';
  if (status === 'scheduled') return 'info';
  return 'default';
}

export function emptyQuizQuestion(questionId = 1) {
  return {
    question_id: questionId,
    question_text: '',
    options: { '1': '', '2': '', '3': '', '4': '' },
    correct_answer: 1,
  };
}

export function emptyFastestFingerRound(roundId = 1) {
  return {
    round_id: roundId,
    prompt: '',
    correct_answer: '',
  };
}

export function emptyChallengeForm(gameType = 'quiz') {
  const base = {
    game_type: gameType,
    title: '',
    subtitle: '',
    card_background_image: '',
    game_icon: '',
    fe_prize_per_participation: 0,
    redirection_link: '',
    game_config: {},
  };

  if (gameType === 'quiz') {
    base.game_config = {
      countdown_seconds: 45,
      questions: [1, 2, 3, 4, 5].map(emptyQuizQuestion),
    };
  } else if (gameType === 'fastest_finger') {
    base.game_config = {
      rounds: [1, 2, 3, 4, 5].map(emptyFastestFingerRound),
    };
  } else if (gameType === 'super_over') {
    base.game_config = {
      objective: '',
      challenge_type: '',
      success_config: {},
    };
  } else {
    base.game_config = {};
  }

  return base;
}

/** Normalize activity or challenge-shaped row for UI tables/forms. */
export function normalizeChallengeActivity(activity) {
  if (!activity || typeof activity !== 'object') return null;
  const config = activity.config && typeof activity.config === 'object' ? activity.config : {};
  const gameType =
    config.game_type ||
    activity.game_type ||
    (activity.activity_type && !['challenge', 'opinion'].includes(activity.activity_type)
      ? activity.activity_type
      : null) ||
    'quiz';
  const gameConfig =
    (config.game_config && typeof config.game_config === 'object' && config.game_config) ||
    (activity.game_config && typeof activity.game_config === 'object' && activity.game_config) ||
    {};

  // Shell fields may live on config or top-level activity columns
  const card_background_image =
    config.card_background_image || activity.card_background_image || activity.image_url || '';
  const game_icon = config.game_icon || activity.game_icon || '';
  const fe_prize_per_participation = Number(
    config.fe_prize_per_participation ??
      activity.fe_prize_per_participation ??
      activity.fan_energy_reward ??
      0
  );
  const redirection_link =
    config.redirection_link ||
    config.deeplink ||
    activity.redirection_link ||
    activity.deeplink ||
    activity.navigation_link ||
    '';
  const game_id = config.game_id || activity.game_id || null;

  return {
    ...activity,
    activity_type: 'challenge',
    game_type: gameType,
    card_background_image,
    game_icon,
    fe_prize_per_participation,
    redirection_link,
    game_id,
    deeplink: config.deeplink || activity.deeplink || redirection_link,
    game_config: gameConfig,
    config,
  };
}

export function challengeToForm(challenge) {
  const normalized = normalizeChallengeActivity(challenge) || challenge;
  const gameType = normalized.game_type || 'quiz';
  const base = emptyChallengeForm(gameType);
  const incoming =
    normalized.game_config && typeof normalized.game_config === 'object'
      ? normalized.game_config
      : {};
  let game_config = { ...base.game_config, ...incoming };

  if (gameType === 'quiz') {
    const qs = Array.isArray(incoming.questions) ? incoming.questions.slice(0, 5) : [];
    while (qs.length < 5) qs.push(emptyQuizQuestion(qs.length + 1));
    game_config = {
      ...game_config,
      questions: qs.map((q, i) => ({
        question_id: q.question_id ?? i + 1,
        question_text: q.question_text || '',
        options: {
          '1': q.options?.['1'] || q.options?.[1] || '',
          '2': q.options?.['2'] || q.options?.[2] || '',
          '3': q.options?.['3'] || q.options?.[3] || '',
          '4': q.options?.['4'] || q.options?.[4] || '',
        },
        correct_answer: Number(q.correct_answer) || 1,
      })),
    };
  }

  if (gameType === 'fastest_finger') {
    const rs = Array.isArray(incoming.rounds) ? incoming.rounds.slice(0, 5) : [];
    while (rs.length < 5) rs.push(emptyFastestFingerRound(rs.length + 1));
    game_config = {
      ...game_config,
      rounds: rs.map((r, i) => ({
        round_id: r.round_id ?? i + 1,
        prompt: r.prompt || r.question_text || '',
        correct_answer: r.correct_answer || '',
      })),
    };
  }

  return {
    ...base,
    id: normalized.id,
    game_type: gameType,
    title: normalized.title || '',
    subtitle: normalized.subtitle || '',
    card_background_image: normalized.card_background_image || '',
    game_icon: normalized.game_icon || '',
    fe_prize_per_participation: Number(normalized.fe_prize_per_participation ?? 0),
    redirection_link: normalized.redirection_link || '',
    game_id: normalized.game_id || null,
    deeplink: normalized.deeplink || '',
    sort_order: normalized.sort_order,
    game_config,
  };
}

/** Activities CRUD body — activity_type=challenge; game lives in config.game_type. */
export function buildChallengePayload(form) {
  let game_config = form.game_config || {};

  if (form.game_type === 'fastest_finger' && Array.isArray(game_config?.rounds)) {
    game_config = {
      ...game_config,
      rounds: game_config.rounds.map((r) => ({
        ...r,
        correct_answer: String(r.correct_answer || '')
          .trim()
          .toUpperCase(),
      })),
    };
  }

  const title = form.title.trim();
  const subtitle = form.subtitle?.trim() || null;
  const card_background_image = form.card_background_image?.trim() || null;
  const game_icon = form.game_icon?.trim() || null;
  const fe_prize_per_participation = Number(form.fe_prize_per_participation) || 0;
  const redirection_link = form.redirection_link?.trim() || '';

  const payload = {
    activity_type: 'challenge',
    title,
    subtitle,
    image_url: card_background_image,
    fan_energy_reward: fe_prize_per_participation,
    completion_mode: 'participation',
    target_value: 1,
    config: {
      surface: 'daily_page',
      game_type: form.game_type,
      card_background_image,
      game_icon,
      icon_url: game_icon,
      image_url: card_background_image,
      fe_prize_per_participation,
      layout: 'hero_media',
      type: form.game_type,
      game_config,
    },
  };
  // Omit empty deeplink so BE can auto-stamp game_id + default dugout:// link
  if (redirection_link) {
    payload.navigation_link = redirection_link;
    payload.config.redirection_link = redirection_link;
    payload.config.deeplink = redirection_link;
  }
  if (form.sort_order != null && form.sort_order !== '') {
    payload.sort_order = Number(form.sort_order);
  }
  return payload;
}

export function validateChallengeForm(form) {
  const errors = [];
  if (!form.title?.trim()) errors.push('Title is required.');
  if (!GAME_TYPES.some((g) => g.value === form.game_type)) {
    errors.push('Pick a valid game type.');
  }

  if (form.game_type === 'quiz') {
    const questions = form.game_config?.questions || [];
    if (questions.length !== 5) errors.push('Quiz needs exactly 5 questions.');
    questions.forEach((q, i) => {
      if (!q.question_text?.trim()) errors.push(`Question ${i + 1} text is required.`);
      const opts = q.options || {};
      ['1', '2', '3', '4'].forEach((k) => {
        if (!String(opts[k] || '').trim()) {
          errors.push(`Question ${i + 1} option ${k} is required.`);
        }
      });
      const ans = Number(q.correct_answer);
      if (![1, 2, 3, 4].includes(ans)) {
        errors.push(`Question ${i + 1} correct_answer must be 1–4.`);
      }
    });
  }

  if (form.game_type === 'fastest_finger') {
    const rounds = form.game_config?.rounds || [];
    if (rounds.length !== 5) errors.push('Fastest Finger needs exactly 5 rounds.');
    rounds.forEach((r, i) => {
      if (!r.prompt?.trim()) errors.push(`Round ${i + 1} prompt is required.`);
      if (!String(r.correct_answer || '').trim()) {
        errors.push(`Round ${i + 1} correct answer is required.`);
      }
    });
  }

  return errors;
}

const OPINION_TITLE_MAX = 70;

/** Pulse-like Daily Opinion form (separate from Pulse feed). */
export function emptyOpinionForm() {
  return {
    title: '',
    summary: '',
    image_url: '',
    hero_presentation: '',
    hero_aspect_ratio: '',
    reward_fe: 5,
    entry_fee_fe: 0,
    status: 'active',
    starts_at: '',
    deadline_at: '',
    expires_at: '',
    sort_order: '',
    options: [
      { id: 'yes', label: 'Agree' },
      { id: 'no', label: 'Disagree' },
    ],
  };
}

export function normalizeOpinionActivity(activity) {
  if (!activity || typeof activity !== 'object') return null;
  const config = activity.config && typeof activity.config === 'object' ? activity.config : {};
  const cards = Array.isArray(config.cards) ? config.cards : [];
  const options = Array.isArray(config.options)
    ? config.options
    : cards[0] && Array.isArray(cards[0].options)
      ? cards[0].options
      : [];
  return {
    ...activity,
    activity_type: 'opinion',
    summary: config.summary || activity.subtitle || '',
    image_url: config.image_url || activity.image_url || config.image_1_url || '',
    hero_presentation: config.hero_presentation || '',
    hero_aspect_ratio: config.hero_aspect_ratio || '',
    entry_fee_fe: config.entry_fee_fe ?? 0,
    reward_fe: activity.fan_energy_reward ?? config.reward_fe ?? 5,
    status: config.status || (activity.is_published === false ? 'draft' : 'active'),
    starts_at: config.starts_at || '',
    deadline_at: config.deadline_at || config.locks_at || '',
    expires_at: config.expires_at || '',
    options,
    cards,
    total_cards_count: activity.total_cards_count ?? (cards.length || 1),
    config,
  };
}

function isoToDatetimeLocal(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(
    d.getMinutes()
  )}`;
}

function datetimeLocalToIso(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

function ensureOpinionOptionIds(options) {
  const seen = new Set();
  return (options || []).map((option, index) => {
    let id = String(option.id || `opt_${index + 1}`).trim();
    const base = id;
    let n = 2;
    while (seen.has(id)) {
      id = `${base}_${n}`;
      n += 1;
    }
    seen.add(id);
    return {
      id,
      label: String(option.label || option.option_text || '').trim(),
    };
  });
}

export function opinionToForm(opinion) {
  const normalized = normalizeOpinionActivity(opinion) || opinion;
  const rawOptions = Array.isArray(normalized.options) ? normalized.options : [];
  const options = rawOptions.length
    ? ensureOpinionOptionIds(
        rawOptions.map((o) => ({
          id: o.id,
          label: o.label || o.option_text || '',
        }))
      )
    : emptyOpinionForm().options;
  return {
    id: normalized.id,
    title: String(normalized.title || '').slice(0, OPINION_TITLE_MAX),
    summary: normalized.summary || normalized.subtitle || '',
    image_url: normalized.image_url || '',
    hero_presentation: normalized.hero_presentation || '',
    hero_aspect_ratio: normalized.hero_aspect_ratio || '',
    reward_fe: normalized.reward_fe ?? normalized.fan_energy_reward ?? 5,
    entry_fee_fe: normalized.entry_fee_fe ?? 0,
    status: normalized.status || 'active',
    starts_at: isoToDatetimeLocal(normalized.starts_at),
    deadline_at: isoToDatetimeLocal(normalized.deadline_at),
    expires_at: isoToDatetimeLocal(normalized.expires_at),
    sort_order: normalized.sort_order,
    options,
  };
}

/** Activities CRUD — Pulse-like opinion card, still activity_type=opinion (not Pulse). */
export function buildOpinionPayload(form) {
  const title = form.title.trim().slice(0, OPINION_TITLE_MAX);
  const summary = form.summary?.trim() || null;
  const image_url = form.image_url?.trim() || null;
  const options = ensureOpinionOptionIds(
    (form.options || []).filter((o) => String(o.label || '').trim() || String(o.id || '').trim())
  );
  const status = form.status === 'draft' ? 'draft' : 'active';
  const reward_fe = form.reward_fe === '' || form.reward_fe == null ? 5 : Number(form.reward_fe) || 0;
  const entry_fee_fe = Number(form.entry_fee_fe) || 0;
  const hero_presentation = form.hero_presentation || null;
  const hero_aspect_ratio = form.hero_aspect_ratio?.trim() || null;
  const starts_at = datetimeLocalToIso(form.starts_at);
  const deadline_at = datetimeLocalToIso(form.deadline_at);
  const expires_at = datetimeLocalToIso(form.expires_at);

  const config = {
    surface: 'daily_page',
    layout: 'prediction_choice',
    status,
    summary,
    image_url,
    hero_presentation,
    hero_aspect_ratio,
    reward_fe,
    entry_fee_fe,
    options,
  };
  if (starts_at) config.starts_at = starts_at;
  if (deadline_at) {
    config.deadline_at = deadline_at;
    config.locks_at = deadline_at;
  }
  if (expires_at) config.expires_at = expires_at;

  const payload = {
    activity_type: 'opinion',
    title,
    subtitle: summary,
    image_url,
    fan_energy_reward: reward_fe,
    completion_mode: 'participation',
    target_value: 1,
    status,
    is_published: status === 'active',
    config,
  };
  if (form.sort_order != null && form.sort_order !== '') {
    payload.sort_order = Number(form.sort_order);
  }
  return payload;
}

export { OPINION_TITLE_MAX };

/** Split GET days/:date activities into challenge vs opinion lists. */
export function splitActivities(activities) {
  const list = Array.isArray(activities) ? activities : [];
  const active = list.filter((a) => a?.is_active !== false);
  const challenges = [];
  const opinions = [];

  active.forEach((row) => {
    const type = String(row.activity_type || '').toLowerCase();
    if (type === 'opinion' || type === 'hot_take' || type === 'prediction') {
      const normalized = normalizeOpinionActivity(row);
      if (normalized) opinions.push(normalized);
      return;
    }
    // challenge section — including legacy quiz/game rows normalized by BE
    if (
      type === 'challenge' ||
      type === 'quiz' ||
      GAME_TYPES.some((g) => g.value === type) ||
      row.config?.game_type
    ) {
      const normalized = normalizeChallengeActivity({
        ...row,
        activity_type: 'challenge',
      });
      if (normalized) challenges.push(normalized);
    }
  });

  return { challenges, opinions };
}

export function validateOpinionForm(form) {
  const errors = [];
  if (!form.title?.trim()) errors.push('Title (the question) is required.');
  if (String(form.title || '').trim().length > OPINION_TITLE_MAX) {
    errors.push(`Title must be ≤${OPINION_TITLE_MAX} characters.`);
  }
  const options = (form.options || []).filter((o) => String(o.label || '').trim());
  if (options.length !== 2 && options.length !== 4) {
    errors.push('Opinion needs exactly 2 or 4 options (like Pulse hot takes).');
  }
  options.forEach((o, i) => {
    const label = String(o.label || '').trim();
    if (label.length < 2 || label.length > 9) {
      errors.push(`Option ${i + 1} label must be 2–9 characters.`);
    }
  });
  return errors;
}

export function emptySector(sortOrder = 1) {
  return {
    sort_order: sortOrder,
    sector_type: 'fe_reward',
    label: '',
    fe_amount: 0,
    weight: 10,
    image_url: null,
  };
}

export function emptyTemplateForm() {
  return {
    title: '',
    spin_count_per_day: 1,
    is_enabled: true,
    sectors: [emptySector(1), emptySector(2)],
  };
}

export function weightPercents(items, weightKey = 'weight') {
  const sum = (items || []).reduce((acc, p) => acc + (Number(p[weightKey]) || 0), 0);
  if (!sum) return (items || []).map(() => 0);
  return (items || []).map((p) => Math.round(((Number(p[weightKey]) || 0) / sum) * 1000) / 10);
}

export function emptyPrize(index = 1) {
  return {
    id: `seg_${index}`,
    label: '',
    image_url: null,
    fan_energy: 0,
    weight: 10,
  };
}

export function parseScheduleDays(payload) {
  return {
    start_date: payload?.start_date || '',
    end_date: payload?.end_date || '',
    today: isDayKey(payload?.today) ? payload.today : istToday(),
    timezone: payload?.timezone || 'Asia/Kolkata',
    days: Array.isArray(payload?.days) ? payload.days : [],
    pagination: payload?.pagination || null,
  };
}

/** Per-IST-date section visibility keys (app GET /dailies/today honors immediately). */
export const SECTION_VISIBILITY_KEYS = [
  {
    key: 'missions',
    label: 'Missions',
    help: 'Missions group empty when off (hub still exists elsewhere)',
  },
  { key: 'fe_wheel', label: 'FE Wheel', help: 'daily_spin: null when off' },
  {
    key: 'daily_challenge',
    label: 'Daily Challenge',
    help: 'Challenge tasks / section hidden',
  },
  { key: 'opinion', label: 'Opinion', help: 'Have Your Say hidden' },
  {
    key: 'fan_of_the_day',
    label: 'Fan of the Day',
    help: 'fan_of_the_day: null when off',
  },
  { key: 'leaderboard', label: 'Leaderboard', help: 'leaderboard: null when off' },
  {
    key: 'weekly_bonus',
    label: 'Weekly bonus',
    help: 'weekly_reward / bonus track omitted',
  },
  {
    key: 'overall_progress',
    label: 'Overall progress',
    help: 'Progress block zeroed for hide',
  },
];

export function defaultSectionsVisible() {
  return SECTION_VISIBILITY_KEYS.reduce((acc, row) => {
    acc[row.key] = true;
    return acc;
  }, {});
}

/**
 * Normalize sections_visible from day aggregate, sections GET, or schedule day.
 * Also accepts legacy top-level daily_challenge_visible / opinion_visible flags.
 */
export function normalizeSectionsVisible(source = {}) {
  const defaults = defaultSectionsVisible();
  const raw =
    source.sections_visible && typeof source.sections_visible === 'object'
      ? source.sections_visible
      : source;
  const next = { ...defaults };
  SECTION_VISIBILITY_KEYS.forEach(({ key }) => {
    if (typeof raw[key] === 'boolean') next[key] = raw[key];
  });
  // Aggregate convenience flags
  if (typeof source.daily_challenge_visible === 'boolean') {
    next.daily_challenge = source.daily_challenge_visible;
  }
  if (typeof source.opinion_visible === 'boolean') {
    next.opinion = source.opinion_visible;
  }
  // Per-section is_visible on nested objects
  if (source.missions && typeof source.missions.is_visible === 'boolean') {
    next.missions = source.missions.is_visible;
  }
  if (source.fe_wheel && typeof source.fe_wheel.is_visible === 'boolean') {
    next.fe_wheel = source.fe_wheel.is_visible;
  }
  if (source.fan_of_the_day && typeof source.fan_of_the_day.is_visible === 'boolean') {
    next.fan_of_the_day = source.fan_of_the_day.is_visible;
  }
  return next;
}

export function hiddenSectionKeys(sectionsVisible) {
  const vis = normalizeSectionsVisible(sectionsVisible || {});
  return SECTION_VISIBILITY_KEYS.filter((row) => vis[row.key] === false).map((row) => row.key);
}
